import { useMemo } from 'react';
import { StanColumnDef } from '@/components/core-components/StanTable/StanTable.types';
import { StanTag } from '@/components';
import { formatCurrency, formatNumber } from '@/lib';
import { useUser } from '@/queries/useUser';
import type { Customer, UseCustomersColumnsProps } from '../types/customers.types';

export function useCustomersColumns({ onRowClick }: UseCustomersColumnsProps): StanColumnDef<Customer>[] {
    const { user } = useUser();
    const currency = user?.data?.currency?.toUpperCase() || 'USD';

    return useMemo(
        () => [
            {
                header: 'Name',
                accessorKey: 'fullName',
                width: '20%',
                cell: info => <span className='text-dark text-label-sm-bold'>{info.getValue() as string}</span>,
            },
            {
                header: 'Email',
                accessorKey: 'email',
                width: '20%',
                hideOnMobile: true,
                cell: info => <span className='text-gray text-label-sm-regular'>{info.getValue() as string}</span>,
            },
            {
                header: 'Since',
                accessorKey: 'fanSinceDate',
                width: '15%',
                hideOnMobile: true,
                cell: info => <span className='text-gray text-label-sm-regular'>{info.getValue() as string}</span>,
            },
            {
                header: 'Purchases',
                accessorKey: 'totalPurchases',
                width: '10%',
                align: 'right',
                hideOnMobile: true,
                cell: info => (
                    <span className='text-dark text-label-sm-regular'>
                        {formatNumber(info.getValue() as number, { decimals: 0 })}
                    </span>
                ),
            },
            {
                header: 'Spent',
                accessorKey: 'totalSpent',
                width: '15%',
                align: 'right',
                cell: info => (
                    <span className='text-positive-fg-primary-strong text-label-sm-bold text-success-main'>
                        {formatCurrency(info.getValue() as number, currency)}
                    </span>
                ),
            },
            {
                header: '',
                accessorKey: 'totalSubscriptions',
                width: '20%',
                hideOnMobile: true,
                cell: info => {
                    const count = info.getValue() as number;
                    if (!count || count === 0) return null;

                    return (
                        <div className='flex justify-end'>
                            <StanTag
                                label={`${count} Subscription${count !== 1 ? 's' : ''}`}
                                className='bg-brand-subtle text-brand-main'
                            />
                        </div>
                    );
                },
            },
        ],
        [onRowClick, currency]
    );
}
